import { User } from 'firebase/auth';
import { firestore } from '../configs/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  DocumentData,
  documentId,
} from 'firebase/firestore';
import { ISubscription } from '@leedpulse-monorepo/interfaces';

export const getPlanById = async (idPlan: string) => {
  try {
    const planDocRef = doc(firestore, 'Plans', idPlan);
    const planDoc = await getDoc(planDocRef);

    if (planDoc.exists()) {
      return {
        success: true,
        status: 'success',
        msg: 'Plan récupéré avec succès.',
        doc: planDoc.data(),
      };
    } else {
      return {
        success: false,
        status: 'error',
        msg: 'Plan non trouvé',
      };
    }
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toLocaleString(),
    };
  }
}

export const getSubscriptionById = async (subscriptionId: string) => {
  try {
    const subscriptionDocRef = doc(firestore, 'Subscriptions', subscriptionId);
    const subscriptionDoc = await getDoc(subscriptionDocRef);

    if (subscriptionDoc.exists()) {
      return {
        success: true,
        status: 'success',
        msg: 'Abonnement récupéré avec succès.',
        doc: subscriptionDoc.data() as ISubscription,
      };
    } else {
      return {
        success: false,
        status: 'error',
        msg: 'Aucun abonnement trouvé',
        doc: undefined,
      };
    }
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toLocaleString(),
      doc: undefined,
    };
  }
}

/**
   * Retrieves multiple subscriptions by their IDs
*/
export const getSubscriptionsByClaims = async (user: User) => {
  try {
    const token = await user.getIdTokenResult();
    const keys = token.claims.api_key;

    const subscriptionsRef = collection(firestore, 'Subscriptions');
    const q = query(subscriptionsRef, where('api_key', 'in', keys));
    const querySnapshot = await getDocs(q);

    const subscriptions = querySnapshot?.docs.map((sub) => sub.data() as ISubscription) || [];
    
    // Collect unique plan IDs from subscriptions
    const uniquePlanIds = [...new Set(subscriptions.map((sub) => sub.id_plan).filter(Boolean))];
    
    if (uniquePlanIds.length === 0) {
      return {
        success: true,
        status: 'success',
        msg: 'Liste des abonnements récupérée avec succès.',
        doc: [],
      };
    }
    
    // Fetch plans with product = "leedpulse" AND id in uniquePlanIds (batched queries due to Firestore 'in' limit of 10)
    const plansRef = collection(firestore, 'Plans');
    const leedpulsePlanIds = new Set<string>();
    const BATCH_SIZE = 10;
    
    for (let i = 0; i < uniquePlanIds.length; i += BATCH_SIZE) {
      const batch = uniquePlanIds.slice(i, i + BATCH_SIZE);
      const plansQuery = query(
        plansRef,
        where(documentId(), 'in', batch),
        where('product', '==', 'leedpulse')
      );
      const plansSnapshot = await getDocs(plansQuery);
      
      plansSnapshot.docs.forEach((planDoc) => {
        leedpulsePlanIds.add(planDoc.id);
      });
    }
    
    // Filter subscriptions whose linked plan has product = "leedpulse"
    const aSubs = subscriptions.filter((sub) => {
      return sub.id_plan && leedpulsePlanIds.has(sub.id_plan);
    });

    return {
      success: true,
      status: 'success',
      msg: 'Liste des abonnements récupérée avec succès.',
      doc: aSubs,
    };
  } catch (error : any) {
    return {
      success: false,
      status: 'error',
      msg: error.message.toLocaleString(),
    };
  }
}

export const updateSubscription = async (oSubscription: ISubscription) => {
  try {
    const docRef = doc(firestore, 'Subscriptions', oSubscription.id);
    await updateDoc(docRef, oSubscription as DocumentData);
    return { success: true };
  } catch (error) {
    return { success: false };
  }
}

export default {
  getPlanById,
  getSubscriptionById,
  getSubscriptionsByClaims,
  updateSubscription,
};